import { QUESTION_TYPES, QUESTION_CONFIGS } from '../constants/question-types';

/**
 * Validate a single question
 * @param {object} question - Question object
 * @returns {object} Validation result { isValid: boolean, errors: Array<string> }
 */
export const validateQuestion = (question) => {
  const errors = [];

  // Check if title exists and is not empty
  if (!question.title || question.title.trim() === '') {
    errors.push('Question title is required');
  }

  // Check if question type is valid
  if (!Object.values(QUESTION_TYPES).includes(question.type)) {
    errors.push('Invalid question type');
  }

  // Validate based on question type
  switch (question.type) {
    case QUESTION_TYPES.MULTIPLE_CHOICE:
    case QUESTION_TYPES.CHECKBOX:
    case QUESTION_TYPES.DROPDOWN:
      // Choice-based questions need at least 2 options and within limits
      const config = QUESTION_CONFIGS[question.type];
      const maxOptions = config.limits.maxOptions;

      if (!question.config?.options || question.config.options.length < 2) {
        errors.push('Choice-based questions must have at least 2 options');
      }
      if (question.config?.options && question.config.options.length > maxOptions) {
        errors.push(`${question.type} questions can have at most ${maxOptions} options`);
      }
      // Check if options are not empty
      if (question.config?.options) {
        question.config.options.forEach((option, index) => {
          if (!option || option.trim() === '') {
            errors.push(`Option ${index + 1} cannot be empty`);
          }
        });

        // For checkbox, validate selection constraints
        if (question.type === QUESTION_TYPES.CHECKBOX) {
          const minSelections = question.config.minSelections || 1;
          const maxSelections = question.config.maxSelections || question.config.options.length;

          if (minSelections > maxSelections) {
            errors.push('Minimum selections cannot be greater than maximum selections');
          }
          if (minSelections < 1 || minSelections > question.config.options.length) {
            errors.push('Minimum selections must be between 1 and the number of options');
          }
          if (maxSelections < 1 || maxSelections > question.config.options.length) {
            errors.push('Maximum selections must be between 1 and the number of options');
          }
        }
      }
      break;

    case QUESTION_TYPES.SHORT_TEXT:
    case QUESTION_TYPES.PARAGRAPH:
      // Text-based questions can have min/max length validation
      if (question.config) {
        const { minLen, maxLen } = question.config;
        const config = QUESTION_CONFIGS[question.type];
        const limits = config.limits;

        if (minLen !== undefined && (minLen < limits.minLen || minLen > limits.maxLen || !Number.isInteger(minLen))) {
          errors.push(`Minimum length must be between ${limits.minLen} and ${limits.maxLen}`);
        }
        if (maxLen !== undefined && (maxLen < limits.minLen || maxLen > limits.maxLen || !Number.isInteger(maxLen))) {
          errors.push(`Maximum length must be between ${limits.minLen} and ${limits.maxLen}`);
        }
        if (minLen !== undefined && maxLen !== undefined && minLen > maxLen) {
          errors.push('Minimum length cannot be greater than maximum length');
        }
      }
      break;

    case QUESTION_TYPES.STATEMENT:
      // Statements don't need additional validation
      break;

    default:
      // For future question types, add validation here
      break;
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate entire form
 * @param {object} form - Form object
 * @returns {object} Validation result { isValid: boolean, errors: Array<{questionIndex: number, errors: Array<string>}> }
 */
export const validateForm = (form) => {
  const errors = [];

  // Validate each question
  if (form.questions && Array.isArray(form.questions)) {
    form.questions.forEach((question, index) => {
      const questionValidation = validateQuestion(question);
      if (!questionValidation.isValid) {
        errors.push({
          questionIndex: index,
          errors: questionValidation.errors,
        });
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Check if form can be auto-saved
 * @param {object} form - Form object
 * @returns {boolean} Whether form can be auto-saved
 */
export const canAutoSave = (form) => {
  // Basic validation for auto-save - just check if title exists
  return form.title && form.title.trim() !== '';
};

/**
 * Get validation summary for display
 * @param {object} validationResult - Result from validateForm
 * @returns {string} Human-readable validation summary
 */
export const getValidationSummary = (validationResult) => {
  if (validationResult.isValid) {
    return 'All validations passed';
  }

  const totalErrors = validationResult.errors.reduce((sum, error) => sum + error.errors.length, 0);
  return `${totalErrors} validation error${totalErrors !== 1 ? 's' : ''} found`;
};
